import localhost from "apis/localhost";
import { alertVariants } from "@visiblethread/react-components/dist/components/Alert";
import {
  addToJobQueue,
  clearJobQueue,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import { showErrorModal } from "slices/app/actions/error";
import {
  clearByContentData,
  getAllByContentData,
  getAllByContentDataAbortController,
} from "slices/discovery/actions/byContent";
import {
  fetchThemes,
  fetchUniqueItemsCount,
  searchItems,
} from "slices/discovery/actions/theme";
import { fetchSnapshots } from "slices/discovery/actions/snapshot";
import {
  deleteTerms,
  getDiscoveryIgnoreListWithFreq,
} from "slices/settings/actions/discoveryIgnoreList";
import { modalTypes } from "constants/types";
import { hideModal } from "slices/app/actions/modals";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { documentBy } from "constants/discovery";
import {
  hideAddToDictPanel,
  setByContentHoveredTerm,
  setDictionaries,
  setDictionary,
  setDictionaryError,
  setNewlyCreatedCategoryName,
  setNewlyCreatedDictionaryId,
  setSelectedCategoryName,
  setSelectedDictionaryId,
  setStep,
  setUniqueItemCount,
} from "slices/discovery/discoverySlice";
import {
  DICTIONARY_NAME_INVALID_CHARACTERS,
  dictionaryDefaultColour,
  dictionaryNotFound,
  FILE_SIZE_INVALID,
} from "constants/dictionary";
import { selectAddToDictPanel, selectConfig } from "slices/discovery/selectors";
import { saveToSS } from "@visiblethread/react-components/dist/utils";
import { applyFilter } from "slices/discovery/actions/filters";

const getAddToIgnoreListSuccessText = (terms, dispatch) => {
  let message;
  if (terms.length > 1) {
    message = `${terms.length} terms successfully added to the Discovery Ignore List`;
  } else {
    message = `"${terms[0]}" successfully added to the Discovery Ignore List`;
  }

  return (
    <>
      <span>{message}</span>
      <Button
        onClick={() => {
          dispatch(setByContentHoveredTerm(null));
          dispatch(deleteTerms(terms));
        }}
        variant={buttonVariants.secondary}
        content="Undo"
      />
    </>
  );
};

export const addToIgnoreList = (terms) => async (dispatch, getState) => {
  dispatch(addToJobQueue("addToIgnoreList"));

  const {
    documentBy: docBy,
    filterType,
    searchTerm,
  } = getState().discovery.config;
  const documentByContent = docBy === documentBy.content;

  await localhost
    .patch("/docs/discoveryIgnoreList/add", terms)
    .then(async () => {
      if (getAllByContentDataAbortController.controller) {
        getAllByContentDataAbortController.controller.abort();
      }

      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: getAddToIgnoreListSuccessText(terms, dispatch),
          dismissTimeout: 8000,
        })
      );

      dispatch(getDiscoveryIgnoreListWithFreq());
      dispatch(fetchSnapshots());
      await dispatch(fetchThemes());
      if (filterType) {
        dispatch(applyFilter(filterType));
        dispatch(fetchUniqueItemsCount(filterType));
      } else if (searchTerm) {
        dispatch(searchItems(searchTerm));
        dispatch(fetchUniqueItemsCount(filterType));
      }

      // Don't clear data for ByContent view, because need to keep current page number and scroll position
      if (documentByContent) {
        dispatch(getAllByContentData());
      } else {
        dispatch(clearByContentData());
      }
    })
    .catch((error) => {
      const message = error?.response?.data?.error;

      if (message) {
        dispatch(
          showGlobalAlert({
            variant: alertVariants.warning,
            text: message,
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(hideModal(modalTypes.IGNORE_TERM_CONFIRM));
      dispatch(removeFromJobQueue("addToIgnoreList"));
    });
};

export const getDictionaries = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/docs/dictionary/list", {})
    .then((response) => {
      const { data } = response;
      dispatch(setDictionaries(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const fetchDictionary = (dictionaryId) => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/docs/dictionary/lock", {
      params: {
        id: dictionaryId,
      },
    })
    .then(async () => {
      await localhost
        .get("/docs/dictionary", {
          params: { dictionaryId },
        })
        .then((response) => {
          const { data } = response;

          // eslint-disable-next-line no-undef
          if (Number(UserInfo.workspaceId) !== data.workspaceId) {
            dispatch(setDictionaryError(dictionaryNotFound));
            return;
          }

          dispatch(setDictionary(data));
          dispatch(setStep(2));
        })
        .catch((error) => {
          if (error.response.status === 404) {
            dispatch(setDictionaryError(dictionaryNotFound));
          } else {
            dispatch(showErrorModal(error));
          }
        })
        .finally(() => {
          dispatch(clearJobQueue());
        });
    })
    .catch((error) => {
      const { errorCode, message } = error.response.data;
      const { step } = selectAddToDictPanel(getState());

      if (
        errorCode === "DICTIONARY_LOCKED" ||
        errorCode === "DICTIONARY_NOT_FOUND"
      ) {
        if (step === 1) {
          dispatch(setDictionaryError(message));
        } else {
          dispatch(setStep(1));
          sessionStorage.removeItem("add-to-dict-panel");
        }
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const unlockDictionary = () => async (dispatch, getState) => {
  const { step, dictionary } = selectAddToDictPanel(getState());

  if (step !== 2 || !dictionary) return;

  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/docs/dictionary/unlock", {
      params: {
        id: dictionary.id,
      },
    })
    .then(() => {})
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const addTerms =
  (termsToAdd, categoryName, insertAt, numberOfRepeatedTerms) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));

    const { folderId } = selectConfig(getState());
    const { dictionary } = selectAddToDictPanel(getState());
    const selectedCategory = dictionary.categories.find(
      ({ name }) => name === categoryName
    );
    const currentTerms = selectedCategory.terms;

    let newTerms;
    if (insertAt === null) {
      newTerms = currentTerms.concat(termsToAdd);
    } else {
      newTerms = [...currentTerms];
      newTerms.splice(insertAt + 1, 0, ...termsToAdd);
    }

    const newDictionary = {
      ...dictionary,
      categories: dictionary.categories.map((category) => {
        if (category.name === categoryName) {
          return {
            ...category,
            terms: newTerms,
          };
        }

        return category;
      }),
    };

    await localhost
      .put(`/docs/dictionary`, newDictionary)
      .then(() => {
        const notAddedMessage =
          numberOfRepeatedTerms === 1
            ? `. 1 term was not added as it already exists in the selected category`
            : `. ${numberOfRepeatedTerms} terms were not added as they already exist in the selected category`;

        const getMessage = () => {
          return termsToAdd.length === 1 ? (
            <span>
              <b>{termsToAdd[0].name}</b> added to the &quot;{dictionary.name}
              &quot; dictionary{numberOfRepeatedTerms !== 0 && notAddedMessage}
            </span>
          ) : (
            <span>
              <b>{termsToAdd.length} items</b> added to the &quot;
              {dictionary.name}&quot; dictionary
              {numberOfRepeatedTerms !== 0 && notAddedMessage}
            </span>
          );
        };

        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: (
              <>
                <span>{getMessage()}</span>
                <Button
                  content="Go to Dictionary"
                  variant={buttonVariants.secondary}
                  onClick={() => {
                    window.location.href = `/dictionaries/#/${dictionary.id}`;
                  }}
                />
              </>
            ),
          })
        );

        saveToSS(
          "add-to-dict-panel",
          JSON.stringify({
            folderId,
            dictionaryId: dictionary.id,
          })
        );
      })
      .catch((error) => {
        const { errorCode, message } = error.response.data;
        if (errorCode === FILE_SIZE_INVALID) {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.danger,
              text: message,
            })
          );
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
        dispatch(hideAddToDictPanel());
      });
  };

export const createNewDictionary = (name, description) => async (dispatch) => {
  // eslint-disable-next-line no-undef
  const { username, workspaceId } = UserInfo;

  const newDictionary = {
    workspaceId,
    updatedBy: username,
    createdBy: username,
    type: "Quality Practices",
    name,
    description,
    comment: `created by ${username}`,
    categories: [
      {
        name: "Default Category",
        description: "",
        terms: [],
      },
    ],
  };

  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post(`/docs/dictionary`, newDictionary)
    .then((response) => {
      dispatch(getDictionaries());
      dispatch(setSelectedDictionaryId(response.data.id));
      dispatch(setNewlyCreatedDictionaryId(response.data.id));
      dispatch(hideModal(modalTypes.CREATE_NEW_DICTIONARY));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `Dictionary "${name}" has been successfully created`,
        })
      );
    })
    .catch((error) => {
      const { errorCode, message } = error.response.data;

      if (
        errorCode === DICTIONARY_NAME_INVALID_CHARACTERS ||
        errorCode === FILE_SIZE_INVALID
      ) {
        dispatch(
          showGlobalAlert({
            variant: alertVariants.danger,
            text: message,
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const createNewCategory =
  (name, description, insertAt) => async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));

    const { dictionary } = selectAddToDictPanel(getState());
    const newCategory = {
      name,
      description,
      colour: dictionaryDefaultColour,
      terms: [],
    };
    const newCategories = [...dictionary.categories];
    newCategories.splice(insertAt, 0, newCategory);
    const newDictionary = {
      ...dictionary,
      categories: newCategories,
    };

    await localhost
      .put(`/docs/dictionary`, newDictionary)
      .then((response) => {
        dispatch(setDictionary(response.data));
        dispatch(setSelectedCategoryName(name));
        dispatch(setNewlyCreatedCategoryName(name));
        dispatch(hideModal(modalTypes.CREATE_NEW_CATEGORY));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `Category "${name}" has been successfully created`,
          })
        );
      })
      .catch((error) => {
        const { errorCode, message } = error.response.data;
        if (errorCode === FILE_SIZE_INVALID) {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.danger,
              text: message,
            })
          );
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };
